import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { DEPT_NAMES } from '@/lib/constants';
import { toast } from 'sonner';
import { PenTool, CheckCircle2, Eye, ChevronDown, ChevronUp, LayoutGrid, List, Search, Hand, Undo2, AlertTriangle } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import KanbanBoard from '@/components/KanbanBoard';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ConfirmDialog } from '@/components/phone/primitives';
import PhoneMyDesigns from './Design/PhoneMyDesigns';

import { PRIORITY_MAP } from '@/lib/status-colors';

const DESIGN_STATUS_MAP = {
  pending_assignment: { label: 'Pending', class: 'bg-slate-100 text-slate-600' },
  assigned: { label: 'Assigned', class: 'bg-blue-50 text-blue-600' },
  in_progress: { label: 'In Progress', class: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', class: 'bg-green-50 text-green-700' },
  not_required: { label: 'N/A', class: 'bg-slate-50 text-slate-400' },
};

export default function MyDesignsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  // ── My Work state ──
  const [orders, setOrders] = useState([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');

  // ── Available Orders state ──
  const [availableOrders, setAvailableOrders] = useState([]);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availablePage, setAvailablePage] = useState(1);
  const [availableSearch, setAvailableSearch] = useState('');
  const [claiming, setClaiming] = useState(null);
  const [releasing, setReleasing] = useState(null);

  // ── Confirm takeover dialog ──
  const [confirmDialog, setConfirmDialog] = useState(null);

  // ── Fetch My Work ──
  const fetchMyWork = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/design/my-designs');
      setOrders(res.data.orders || []);
      setRecentlyCompleted(res.data.recently_completed || []);
    } catch { toast.error('Failed to load designs'); }
    setLoading(false);
  }, []);

  // ── Fetch Available Orders ──
  const fetchAvailable = useCallback(async () => {
    setAvailableLoading(true);
    try {
      // the pile is what nobody holds — an order somebody has taken is theirs,
      // and it belongs on their work, not on everyone's list to take again
      const params = new URLSearchParams({
        page: availablePage, limit: 30, assignment_status: 'unassigned',
      });
      if (availableSearch) params.set('search', availableSearch);
      const res = await api.get(`/design/available-orders?${params}`);
      setAvailableOrders(res.data.orders || []);
      setAvailableTotal(res.data.total || 0);
    } catch { toast.error('Failed to load available orders'); }
    setAvailableLoading(false);
  }, [availablePage, availableSearch]);

  useEffect(() => { fetchMyWork(); }, [fetchMyWork]);
  // the available pile is a rail now, not a tab, so it always loads
  useEffect(() => { fetchAvailable(); }, [fetchAvailable]);

  // ── Design completion handlers ──
  const handleCompleteAll = async (orderId) => {
    setCompleting(orderId);
    try {
      await api.put(`/design/${orderId}/complete`);
      toast.success('Design marked as complete for your items');
      fetchMyWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to complete'); }
    setCompleting(null);
  };

  const handleCompleteItem = async (orderId, lineItemId) => {
    setCompleting(`${orderId}-${lineItemId}`);
    try {
      const res = await api.put(`/design/${orderId}/line-items/${lineItemId}/design-complete`);
      if (res.data.all_design_done) {
        toast.success('All items designed — order moved to production!');
      } else {
        toast.success('Item design completed');
      }
      fetchMyWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to complete'); }
    setCompleting(null);
  };

  // ── Claim handlers ──
  const handleClaimItem = async (orderId, lineItemId, confirm = false) => {
    setClaiming(`${orderId}-${lineItemId}`);
    try {
      const res = await api.post(`/design/${orderId}/line-items/${lineItemId}/claim`, { confirm });
      if (res.data.status === 'needs_confirmation') {
        setConfirmDialog({
          orderId, lineItemId,
          currentDesigner: res.data.current_designer_name,
          orderDisplayId: res.data.order_id,
        });
      } else {
        toast.success(res.data.message);
        fetchAvailable();
        fetchMyWork();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim');
    }
    setClaiming(null);
  };

  const handleClaimOrder = async (orderId, confirm = false) => {
    setClaiming(orderId);
    try {
      const res = await api.post(`/design/${orderId}/claim`, { confirm });
      toast.success(res.data.message);
      fetchAvailable();
      fetchMyWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to claim'); }
    setClaiming(null);
  };

  const handleConfirmTakeover = async () => {
    if (!confirmDialog) return;
    await handleClaimItem(confirmDialog.orderId, confirmDialog.lineItemId, true);
    setConfirmDialog(null);
  };

  const handleReleaseItem = async (orderId, lineItemId) => {
    setReleasing(`${orderId}-${lineItemId}`);
    try {
      const res = await api.post(`/design/${orderId}/line-items/${lineItemId}/release`);
      toast.success(res.data.message);
      fetchAvailable();
      fetchMyWork();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to release'); }
    setReleasing(null);
  };

  // ── Filters ──
  const filteredOrders = useMemo(() => {
    if (!searchQuery) return orders;
    const q = searchQuery.toLowerCase();
    return orders.filter(o => o.order_id?.toLowerCase().includes(q) || o.voice_number?.toLowerCase().includes(q) || o.customer_name?.toLowerCase().includes(q) || o.firm_name?.toLowerCase().includes(q));
  }, [orders, searchQuery]);

  const filteredCompleted = useMemo(() => {
    if (!searchQuery) return recentlyCompleted;
    const q = searchQuery.toLowerCase();
    return recentlyCompleted.filter(o => o.order_id?.toLowerCase().includes(q) || o.customer_name?.toLowerCase().includes(q));
  }, [recentlyCompleted, searchQuery]);

  const isItemMine = (item, order) => {
    return item.is_mine || item.assigned_designer_id === user?.id ||
           (!item.assigned_designer_id && order.assigned_designer_id === user?.id);
  };

  const getMyItemsCount = (order) => {
    return (order.line_items || []).filter(item =>
      isItemMine(item, order) &&
      item.design_status !== 'completed' &&
      item.design_status !== 'not_required'
    ).length;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div></div>;
  }

  // My work and the available pile, side by side — deciding whether to pull more
  // work is exactly the moment you need to see both, and they used to be tabs.
  const myRows = [];
  for (const order of filteredOrders) {
    myRows.push({ ...order, __kind: 'order', __rowId: order.id });
    if (expandedOrder === order.id) {
      for (const item of order.line_items || []) {
        myRows.push({ ...item, __kind: 'item', __depth: 1, __order: order, __rowId: `${order.id}:${item.id}` });
      }
    }
  }

  const toggleOrder = (id) => setExpandedOrder(current => (current === id ? null : id));

  // taking work off another designer is asked the same way on both screens
  const takeoverDialog = isMobile ? (
    <ConfirmDialog
      open={!!confirmDialog}
      title={`Take this from ${confirmDialog?.currentDesigner || 'another designer'}?`}
      message="It moves to your queue and off theirs. They are not notified, so tell them."
      confirmLabel="Take it"
      cancelLabel="Cancel"
      onConfirm={handleConfirmTakeover}
      onCancel={() => setConfirmDialog(null)}
    />
  ) : (
    <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Take this from {confirmDialog?.currentDesigner || 'another designer'}?
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          It moves to your queue and off theirs. They are not notified, so tell them.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button onClick={handleConfirmTakeover} className="bg-brand-orange text-white hover:bg-brand-orange-dark">
            Take it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // the work and the pile as tabs rather than side by side; the same handlers
  if (isMobile) {
    return (
      <>
        <PhoneMyDesigns
          user={user}
          orders={filteredOrders}
          recentlyCompleted={filteredCompleted}
          openCountOf={getMyItemsCount}
          setSearchQuery={setSearchQuery}
          expandedOrder={expandedOrder}
          toggleOrder={toggleOrder}
          completing={completing}
          releasing={releasing}
          claiming={claiming}
          available={availableOrders}
          availableTotal={availableTotal}
          availableLoading={availableLoading}
          availablePage={availablePage}
          setAvailablePage={setAvailablePage}
          setAvailableSearch={setAvailableSearch}
          onCompleteAll={handleCompleteAll}
          onCompleteItem={handleCompleteItem}
          onRelease={handleReleaseItem}
          onClaimOrder={handleClaimOrder}
          onOpenOrder={(order) => navigate(`/orders/${order.id}`)}
          onRefresh={() => { fetchMyWork(); fetchAvailable(); }}
        />
        {takeoverDialog}
      </>
    );
  }

  const myColumns = [
    {
      key: 'name',
      header: 'Item / Customer',
      width: 'minmax(14rem,1.6fr)',
      render: (row) => {
        if (row.__kind === 'item') {
          const status = DESIGN_STATUS_MAP[row.design_status] || DESIGN_STATUS_MAP.pending_assignment;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-slate-700">
                {row.item_name || row.product_subtype || DEPT_NAMES[row.order_type] || 'Item'}
              </span>
              <Badge className={`shrink-0 text-[9px] ${status.class}`}>{status.label}</Badge>
            </span>
          );
        }
        const mine = getMyItemsCount(row);
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleOrder(row.id); }}
              aria-label={expandedOrder === row.id ? 'Collapse items' : 'Expand items'}
              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              {expandedOrder === row.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-slate-900">
                {row.line_items?.[0]?.item_name || row.customer_name}
              </span>
              <span className="block truncate text-[11px] leading-tight text-slate-500">
                {row.customer_name}{row.firm_name ? ` — ${row.firm_name}` : ''}
              </span>
            </span>
            {mine > 0 && <span className="shrink-0 text-[10px] text-slate-400">{mine} open</span>}
          </span>
        );
      },
    },
    {
      key: 'order_id',
      header: 'Order',
      width: '9.5rem',
      render: (row) => row.__kind === 'item'
        ? <span className="text-[11px] text-slate-400">Qty {row.quantity?.toLocaleString()}</span>
        : <span className="block truncate text-[11px] text-slate-400">{row.order_id}</span>,
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '5.5rem',
      render: (row) => row.__kind === 'item' ? null : (
        <span className={`text-[11px] font-medium ${
          row.priority === 'high' ? 'text-red-600' : row.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
        }`}>{PRIORITY_MAP[row.priority]?.label || row.priority}</span>
      ),
    },
    {
      key: 'expected_delivery_date',
      header: 'Delivery',
      width: '7rem',
      render: (row) => row.__kind === 'item' ? null : (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {row.expected_delivery_date ? formatIST(row.expected_delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '11rem',
      align: 'right',
      render: (row) => {
        if (row.__kind === 'item') {
          const done = row.design_status === 'completed' || row.design_status === 'not_required';
          return done ? null : (
            <span className="flex items-center justify-end gap-0.5">
              <CellButton
                icon={CheckCircle2}
                label="Mark done"
                tone="good"
                disabled={completing === `${row.__order.id}-${row.id}`}
                onClick={() => handleCompleteItem(row.__order.id, row.id)}
              />
              <CellButton
                icon={Undo2}
                label="Give back"
                disabled={releasing === `${row.__order.id}-${row.id}`}
                onClick={() => handleReleaseItem(row.__order.id, row.id)}
              />
            </span>
          );
        }
        return (
          <span className="flex items-center justify-end gap-0.5">
            {getMyItemsCount(row) > 0 && (
              <CellButton
                icon={CheckCircle2}
                label="All done"
                tone="good"
                disabled={completing === row.id}
                onClick={() => handleCompleteAll(row.id)}
              />
            )}
            <CellButton icon={Eye} label="Open order" iconOnly onClick={() => navigate(`/orders/${row.id}`)} />
          </span>
        );
      },
    },
  ];

  const availableColumns = [
    {
      key: 'name',
      header: 'Item / Customer',
      width: 'minmax(11rem,1.5fr)',
      render: (o) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {o.line_items?.[0]?.item_name || o.customer_name}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-500">
            {o.order_id}{o.firm_name ? ` · ${o.firm_name}` : ''}
          </span>
        </>
      ),
    },
    {
      key: 'claim',
      header: '',
      width: '5.5rem',
      align: 'right',
      render: (o) => (
        <CellButton
          icon={Hand}
          label="Take"
          tone="accent"
          disabled={claiming === o.id}
          onClick={() => handleClaimOrder(o.id)}
        />
      ),
    },
  ];

  return (
    <Screen data-testid="my-designs-page">
      <CommandBar
        title="Designer Workspace"
        subtitle={user?.full_name}
        actions={[
          {
            key: 'view',
            label: viewMode === 'list' ? 'Kanban' : 'List',
            icon: viewMode === 'list' ? LayoutGrid : List,
            onClick: () => setViewMode(viewMode === 'list' ? 'kanban' : 'list'),
          },
          { key: 'refresh', label: 'Refresh', icon: PenTool, onClick: () => { fetchMyWork(); fetchAvailable(); } },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Order, customer…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'orders with my work', value: filteredOrders.length, tone: 'blue' },
          {
            key: null,
            label: 'items open',
            value: filteredOrders.reduce((s, o) => s + getMyItemsCount(o), 0),
            tone: 'warn',
          },
          { key: null, label: 'done recently', value: filteredCompleted.length, tone: 'good' },
          { key: null, label: 'available to take', value: availableTotal || availableOrders.length },
        ]}
      />

      <ScreenSplit
        railWidth="20rem"
        rail={
          <>
            <Pane
              title="Available to take"
              aside={`${availableOrders.length}`}
              className="min-h-0 flex-1"
              scroll={false}
              bodyClassName="flex flex-col"
              actions={
                <span className="relative">
                  <Search className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-white/60" />
                  <Input
                    value={availableSearch}
                    onChange={(e) => { setAvailableSearch(e.target.value); setAvailablePage(1); }}
                    placeholder="find…"
                    aria-label="Search available orders"
                    className="h-5 w-24 border-white/30 bg-white/10 pl-5 text-[11px] text-white placeholder:text-white/50"
                  />
                </span>
              }
            >
              <DataGrid
                columns={availableColumns}
                rows={availableOrders}
                loading={availableLoading}
                getRowId={(o) => o.id}
                empty="Nothing waiting to be picked up"
              />
            </Pane>

            {filteredCompleted.length > 0 && (
              <Pane title="Recently completed" aside={`${filteredCompleted.length}`} collapsible defaultOpen={false}>
                {filteredCompleted.slice(0, 12).map(o => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => navigate(`/orders/${o.id}`)}
                    className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left last:border-b-0 hover:bg-slate-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px] text-slate-700">{o.customer_name}</span>
                      <span className="block truncate text-[10px] text-slate-400">{o.order_id}</span>
                    </span>
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  </button>
                ))}
              </Pane>
            )}
          </>
        }
      >
        {viewMode === 'kanban' ? (
          <Pane title="My board" bodyClassName="p-3">
            <KanbanBoard
              columns={[
                { id: 'assigned', title: 'To start', color: 'slate',
                  orders: filteredOrders.filter(o => o.design_status !== 'in_progress' && o.design_status !== 'completed') },
                { id: 'in_progress', title: 'In progress', color: 'amber',
                  orders: filteredOrders.filter(o => o.design_status === 'in_progress') },
                { id: 'completed', title: 'Completed', color: 'green', orders: filteredCompleted },
              ]}
              onColumnChange={() => toast.info('Mark items done from the list — the row carries the action')}
            />
          </Pane>
        ) : (
          <Pane
            title="My work"
            aside={`${filteredOrders.length} order${filteredOrders.length === 1 ? '' : 's'}`}
            scroll={false}
            bodyClassName="flex flex-col"
          >
            <DataGrid
              data-testid="my-designs-table"
              columns={myColumns}
              rows={myRows}
              loading={loading}
              getRowId={(row) => row.__rowId}
              empty={
                <span className="flex flex-col items-center gap-2 text-slate-400">
                  <PenTool className="h-8 w-8 text-slate-300" />
                  Nothing assigned to you — take something from the rail.
                </span>
              }
            />
          </Pane>
        )}
      </ScreenSplit>

      {takeoverDialog}
    </Screen>
  );
}
